<template>
  <div class="annual-plan">
    <div class="form-container">
      <h2>年度计划管理</h2>
      
      <form @submit.prevent="handleSubmit">
        <!-- 年份选择 -->
        <div class="form-group">
          <label>年份：</label>
          <select v-model="formData.year" required>
            <option v-for="year in yearOptions" :key="year" :value="year">{{ year }}年</option>
          </select>
        </div>

        <!-- 计划类型选择 -->
        <div class="form-group">
          <label>计划类型：</label>
          <div class="radio-group">
            <label>
              <input type="radio" v-model="formData.planType" :value="2" @change="handlePlanTypeChange">
              部门计划
            </label>
            <label>
              <input type="radio" v-model="formData.planType" :value="3" @change="handlePlanTypeChange">
              员工计划
            </label>
          </div>
        </div>

        <!-- 部门/员工选择 -->
        <div class="form-group">
          <label>{{ formData.planType === 2 ? '部门' : '员工' }}：</label>
          <select 
            v-model="formData.targetId" 
            required
            @change="handleTargetChange"
          >
            <option value="">请选择{{ formData.planType === 2 ? '部门' : '员工' }}</option>
            <option v-show="formData.planType === 2"
              v-for="item in departments " 
              :key="item.deptId" 
              :value="item.deptId"
            >
              {{ item.deptName }}
            </option>
            <option v-show="formData.planType === 3"
              v-for="item in employees" 
              :key="item.id" 
              :value="item.id"
            >
              {{ item.name }}
            </option>
          </select>
        </div>

        <!-- 年度目标金额 -->
        <div class="form-group">
          <label>年度目标金额：</label>
          <input 
            type="number" 
            v-model.number="formData.annualTarget" 
            required 
            min="0" 
            step="0.01"
          >
          <span class="unit">元</span>
          <el-button type="primary" size="small" @click="handleAnnualTargetChange">
              <i class="el-icon-plus"></i> 一键分配
            </el-button>
        </div>

        <!-- 月度目标设置 -->
        <div class="monthly-targets">
          <h3>月度目标设置</h3>
          <div class="monthly-grid">
            <div v-for="month in 12" :key="month" class="monthly-item">
              <label>{{ month }}月：</label>
              <input 
                type="number" 
                v-model.number="formData.monthlyTargets[month-1]" 
                required 
                min="0" 
                step="0.01"
                @input="handleMonthlyTargetChange"
              >
              <span class="unit">元</span>
            </div>
          </div>
        </div>

        <!-- 月度目标总和提示 -->
        <!-- <div v-if="showMonthlySumWarning" class="warning-message">
          月度目标总和与年度目标不一致
        </div> -->

        <!-- 按钮组 -->
        <div class="button-group">
          <button type="submit" class="btn-primary">保存</button>
          <button type="button" class="btn-default" @click="handleReset">重置</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { getDepartments, getEmployees, createAnnualPlan, getTarget } from '@/api/annualPlan'

export default {
  name: 'AnnualPlan',
  setup() {
    // 年份选项
    const currentYear = new Date().getFullYear()
    const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear + i)

    // 表单数据
    const formData = reactive({
      year: currentYear,
      planType: 2,
      targetId: '',
      annualTarget: 0,
      monthlyTargets: Array(12).fill(0)
    })

    // 部门列表
    const departments = ref([])
    // 员工列表
    const employees = ref([])

    // 计算月度目标总和
    const monthlySum = computed(() => {
      return formData.monthlyTargets.reduce((sum, target) => sum + target, 0)
    })

    // 显示月度目标总和警告
    const showMonthlySumWarning = computed(() => {
      return Math.abs(monthlySum.value - formData.annualTarget) > 0.01
    })

    // 获取部门列表
    const fetchDepartments = async () => {
      try {
        const response = await getDepartments()
        departments.value = response.data
      } catch (error) {
        alert('获取部门列表失败')
      }
    }

    // 获取员工列表
    const fetchEmployees = async () => {
      try {
        const response = await getEmployees()
        employees.value = response.data
      } catch (error) {
        alert('获取员工列表失败')
      }
    }

    // 处理计划类型变更
    const handlePlanTypeChange = () => {
      formData.targetId = ''
    }

    // 处理目标选择变更
    const handleTargetChange = async () => {
      if (!formData.targetId) return
      
      try {
        let params = {
          targetId: formData.targetId,
          planType: formData.planType,
          year: formData.year
        }
        const response = await getTarget(params)
        if (response.data) {
          // 更新表单数据
          formData.annualTarget = response.data.annualTarget || 0
          formData.monthlyTargets = response.data.monthlyTargets || Array(12).fill(0)
        }
      } catch (error) {
        console.error('获取目标数据失败:', error)
        alert('获取目标数据失败')
      }
    }

    // 处理年度目标变更
    const handleAnnualTargetChange = () => {
      if (formData.annualTarget > 0) {
        const monthlyTarget = formData.annualTarget / 12
        formData.monthlyTargets = Array(12).fill(Number(monthlyTarget.toFixed(2)))
      }
    }

    // 处理月度目标变更
    const handleMonthlyTargetChange = () => {
      // 计算月度目标总和并更新年度目标
      const sum = formData.monthlyTargets.reduce((total, target) => total + (Number(target) || 0), 0)
      formData.annualTarget = Number(sum.toFixed(2))
    }

    // 处理表单提交
    const handleSubmit = async () => {
      try {
        const data = {
          year: formData.year,
          planType: formData.planType,
          targetId: formData.targetId,
          annualTarget: formData.annualTarget,
          monthlyTargets: formData.monthlyTargets
        }
        await createAnnualPlan(data)
        alert('保存成功')
        handleReset()
      } catch (error) {
        alert('保存失败')
      }
    }

    // 重置表单
    const handleReset = () => {
      formData.year = currentYear
      formData.planType = 2
      formData.targetId = ''
      formData.annualTarget = 0
      formData.monthlyTargets = Array(12).fill(0)
    }

    onMounted(() => {
      fetchDepartments()
      fetchEmployees()
    })

    return {
      yearOptions,
      formData,
      departments,
      employees,
      showMonthlySumWarning,
      handlePlanTypeChange,
      handleTargetChange,
      handleAnnualTargetChange,
      handleMonthlyTargetChange,
      handleSubmit,
      handleReset
    }
  }
}
</script>

<style scoped>
.annual-plan {
  padding: 20px;
}

.form-container {
  max-width: 800px;
  margin: 0 auto;
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

h2 {
  margin-bottom: 20px;
  color: #333;
}

h3 {
  margin: 20px 0;
  color: #666;
}

.form-group {
  margin-bottom: 20px;
}

label {
  display: inline-block;
  width: 120px;
  margin-right: 10px;
}

select, input[type="number"] {
  width: 200px;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.unit {
  margin-left: 5px;
  color: #666;
}

.radio-group {
  display: inline-block;
}

.radio-group label {
  width: auto;
  margin-right: 20px;
}

.monthly-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
}

.monthly-item {
  display: flex;
  align-items: center;
}

.monthly-item label {
  width: 60px;
}

.monthly-item input {
  width: 120px;
}

.warning-message {
  color: #ff4d4f;
  margin: 10px 0;
  padding: 10px;
  background: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 4px;
}

.button-group {
  margin-top: 20px;
  text-align: center;
}

button {
  padding: 8px 20px;
  margin: 0 10px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.btn-primary {
  background: #1890ff;
  color: white;
}

.btn-primary:hover {
  background: #40a9ff;
}

.btn-default {
  background: #f0f0f0;
  color: #333;
}

.btn-default:hover {
  background: #d9d9d9;
}
</style> 